import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type IncomeEntry, type OtherIncomeFormType, otherIncomesFormSchema } from "Forms/Schemas/Budget/Incomes/otherIncomesFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useCreateIncome,
  useDeleteIncome,
  useGetIncome,
  useUpdateIncome,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Income, IncomeAmountAmountType, IncomeBodyIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  clientId?: string;
  edit?: Income;
};

export const OtherIncomeForm = forwardRef<{}, Props>(({ onClose, clientId, edit }, ref) => {
  const { t } = useResource();
  const theme = useTheme();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const queryClient = useQueryClient();
  const { data: editIncomeData } = useGetIncome(analysisId, versionId, edit?.guid ?? "", {
    query: { enabled: !!edit },
  });
  const { mutate: putIncome } = useUpdateIncome();
  const { mutate } = useCreateIncome();
  const { mutate: deleteIncome } = useDeleteIncome();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<OtherIncomeFormType>({
    resolver: yupResolver(otherIncomesFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      incomes: [{ incomeName: "", amountValue: undefined }],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "incomes",
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteIncome: () => {
      handleDelete();
    },
  }));

  useEffect(() => {
    if (editIncomeData && edit) {
      replace(
        editIncomeData.incomeAmounts.map((income) => ({
          incomeName: income.name,
          amountValue: income.amount.value.toString() || ("" as any),
        })),
      );
    }
  }, [editIncomeData, edit, replace]);

  const onSubmit = async (data: OtherIncomeFormType) => {
    if (edit) {
      putIncome(
        {
          analysisId,
          versionId,
          incomeId: edit.guid,
          data: {
            incomeType: IncomeBodyIncomeType.other,
            clientGuid: clientId ?? "",
            incomeAmounts: data.incomes.map((income: IncomeEntry) => ({
              amountType: IncomeAmountAmountType.other,
              amount: {
                value: income.amountValue ?? 0,
                currencyCode: "CZK",
                frequency: AmountFrequency.monthly,
              },
              name: income.incomeName,
            })),
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    } else {
      mutate(
        {
          data: {
            incomeType: IncomeBodyIncomeType.other,
            clientGuid: clientId ?? "",
            incomeAmounts: data.incomes.map((income: IncomeEntry) => ({
              amountType: IncomeAmountAmountType.other,
              amount: {
                value: income.amountValue ?? 0,
                currencyCode: "CZK",
                frequency: AmountFrequency.monthly,
              },
              name: income.incomeName,
            })),
          },
          analysisId,
          versionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleDelete = () => {
    deleteIncome(
      {
        analysisId,
        versionId,
        incomeId: edit?.guid ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
        },
      },
    );
  };

  const addNewIncome = () => {
    append({ incomeName: "", amountValue: undefined as any });
  };

  return (
    <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
      <Box>
        <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
          {t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Other)}
        </Typography>

        {fields.map((field, index) => (
          <Grid container spacing={Spacing.XL} key={field.id} mb={Spacing.XL}>
            <Grid size={5}>
              <BlFormInput
                control={control}
                disabled={readOnly}
                name={`incomes.${index}.incomeName`}
                label={t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.IncomeName)}
                errors={errors}
                labelEndAdornment={<StyledLabelAdornment />}
                placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
              />
            </Grid>

            <Grid size={5}>
              <BlFormInput
                control={control}
                disabled={readOnly}
                name={`incomes.${index}.amountValue`}
                label={t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.IncomeAmount)}
                errors={errors}
                labelEndAdornment={<StyledLabelAdornment />}
                inputEndAdornment={
                  <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                }
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                    min: 0,
                  },
                ]}
                placeholder="0"
              />
            </Grid>

            <Grid size={1} display="flex" alignItems="flex-start" mt={5} justifyContent="center">
              <Button variant="text" onClick={() => remove(index)} disabled={fields.length === 1 || readOnly}>
                <BinIcon />
              </Button>
            </Grid>
          </Grid>
        ))}

        <Button
          variant="text"
          colorCustom={theme.colors.brandPrimary}
          startAdornment={<AddIcon />}
          disabled={readOnly}
          onClick={addNewIncome}
        >
          {t(Resources.AnalysisDetail.Budget.Incomes.Form.Buttons.AddAnother)}
        </Button>
      </Box>
    </Stack>
  );
});
